import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx"; // ✅ Import XLSX to read Excel file

function SelectExelRows({ width, height, file, onSelectionsChange, onUnitChange }) {
  const [columns, setColumns] = useState([]); // ✅ Store column names from Excel
  const [dropdown1, setDropdown1] = useState("");
  const [dropdown2, setDropdown2] = useState("");
  const [dropdown3, setDropdown3] = useState("");
  const [dataUnit, setDataUnit] = useState(""); // ✅ Store selected unit
  const [loading, setLoading] = useState(false);

  // ✅ Allowed units for selection
  const units = ["s", "m", "h", "d"];

  // ✅ Read Excel file and extract column names from the first row
  useEffect(() => {
    if (onUnitChange) {
      console.log("[DEBUG] Sending unit to parent:", dataUnit);  // ✅ Debug log
      onUnitChange(dataUnit);
    }
  }, [dataUnit, onUnitChange]);

  useEffect(() => {
    setLoading(true); // Start loading when file is read
    const reader = new FileReader();

    if (file.name.endsWith(".csv")) {
      reader.readAsText(file);
      reader.onload = (e) => {
        const csvData = e.target.result.split("\n").map(row => row.split(","));
        if (csvData.length > 0) {
          setColumns(csvData[0]);
        }
        setLoading(false); // ✅ Stop loading when done
      };
    } else {
      reader.readAsArrayBuffer(file);
      reader.onload = (e) => {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length > 0) {
          setColumns(jsonData[0]);
        }
        setLoading(false); // ✅ Stop loading when done
      };
    }
  }, [file]);

  useEffect(() => {
    onSelectionsChange &&
      onSelectionsChange({
        timestamp: dropdown1,
        positionID: dropdown2,
        unitID: dropdown3,
      });
  }, [dropdown1, dropdown2, dropdown3, onSelectionsChange]); // ✅ Calls parent when values change

  useEffect(() => {
    onUnitChange && onUnitChange(dataUnit); // ✅ Send selected unit to parent
  }, [dataUnit, onUnitChange]);

  return (
    <div style={{ ...styles.container, width: `${width}px`, height: `${height}px` }}>
      {/* Title = Selected File Name */}
      <div style={styles.titleContainer}>
        <h3 style={styles.title}>{file.name}</h3>
      </div>

      {/* Show Spinner While Loading */}
      {loading ? (
        <div style={styles.spinnerContainer}>
          <div className="spinner"></div>
          <p>Loading file...</p>
        </div>
      ) : (
        <div style={styles.dropdownContainer}>
          {/* ✅ Row with Timestamp and Unit Selector */}
          <div style={styles.row}>
            <div style={styles.labelContainer}>
              <span style={styles.label}>
                Timestamp{" "}
                <span
                  style={styles.infoIcon}
                  title="Timestamp: Column representing the time information.">
                  ⓘ
                </span>
              </span>
            </div>
            <select
              style={styles.dropdown}
              value={dropdown1}
              onChange={(e) => setDropdown1(e.target.value)}>
              <option value="">Select: timestamp</option>
              {columns.map((col, index) => (
                <option key={index} value={col}>
                  {col}
                </option>
              ))}
            </select>
            <select
              style={styles.smallDropdown}
              value={dataUnit}
              onChange={(e) => setDataUnit(e.target.value)}>
              <option value="">Unit</option>
              {units.map((unit, index) => (
                <option key={index} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>

          {/* ✅ Row for PositionID */}
          <div style={styles.row}>
            <div style={styles.labelContainer}>
              <span style={styles.label}>
                PositionID{" "}
                <span
                  style={styles.infoIcon}
                  title="PositionID: Column indicating position information.">
                  ⓘ
                </span>
              </span>
            </div>
            <select
              style={styles.dropdown}
              value={dropdown2}
              onChange={(e) => setDropdown2(e.target.value)}>
              <option value="" disabled hidden>
                Select: positionID
              </option>
              {columns.map((col, index) => (
                <option key={index} value={col}>
                  {col}
                </option>
              ))}
            </select>
          </div>

          {/* ✅ Row for UnitID */}
          <div style={styles.row}>
            <div style={styles.labelContainer}>
              <span style={styles.label}>
                UnitID{" "}
                <span
                  style={styles.infoIcon}
                  title="UnitID: Column for unique identifiers of units.">
                  ⓘ
                </span>
              </span>
            </div>
            <select
              style={styles.dropdown}
              value={dropdown3}
              onChange={(e) => setDropdown3(e.target.value)}>
              <option value="">Select: unitID</option>
              {columns.map((col, index) => (
                <option key={index} value={col}>
                  {col}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  spinnerContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "100%",
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "5px solid rgba(0, 0, 0, 0.1)",
    borderTop: "5px solid #007bff",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  container: {
    border: "2px solid #ccc",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    borderRadius: "10px",
    backgroundColor: "#f9f9f9",
    fontFamily: "'Times New Roman', sans-serif",
    padding: "10px",
    height: "100%",
  },
  titleContainer: {
    width: "100%",
    paddingTop: "5px",
    borderBottom: "1px solid #ccc",
    textAlign: "center",
  },
  title: {
    fontSize: "18px",
    fontWeight: "bold",
    fontFamily: "'Times New Roman', sans-serif",
    margin: "0",
  },
  dropdownContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    width: "80%",
    flex: 1,
    justifyContent: "center",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  dropdown: {
    flex: 1,
    padding: "1%",
    fontSize: "14px",
    borderRadius: "5px",
    border: "1px solid #aaa",
    textAlign: "center",
  },
  smallDropdown: {
    padding: "1%",
    fontSize: "14px",
    borderRadius: "5px",
    border: "1px solid #aaa",
    textAlign: "center",
  },
  labelContainer: {
    width: "120px",
    textAlign: "right",
  },
  label: {
    fontSize: "14px",
    fontWeight: "bold",
    display: "inline-flex",
    alignItems: "center",
    cursor: "help",
  },
  infoIcon: {
    fontSize: "10px",
    verticalAlign: "super",
    marginLeft: "2px",
  },
};

export default SelectExelRows;
